import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Bot, ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchGroupBots, type AdminMember } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A4.12 — Ulangan botlar. GET /admin/groups/:id/members, faqat `is_bot`
 * (A4.06 adminlar naqshining ko'zgusi). Members javobiga `is_bot` qo'shilgach
 * (lane-A backend patch) jonli — hozircha halol empty-state.
 */
export function GroupBots() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminMember>[]>(
    () => [
      {
        key: 'user',
        header: t.col.name,
        type: 'text',
        minWidth: 180,
        sortable: false,
        render: (r) => {
          const label =
            [r.first_name, r.last_name].filter(Boolean).join(' ') ||
            (r.username ? `@${r.username}` : `#${r.user_id}`)
          return (
            <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
              {label}
            </Link>
          )
        },
      },
      { key: 'username', header: t.col.username, type: 'text', width: 150 },
      { key: 'custom_title', header: t.groups.customTitle, type: 'text', minWidth: 150 },
      { key: 'joined_at', header: t.groups.joined, type: 'date', width: 150 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupBots(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.groups.botsTitle}
        screenId="A4.12"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminMember>
        screenId="A4.12"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.user_id)}
        exportable
        emptyState={{ icon: <Bot size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
